import React, { useState, useEffect, useContext, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useParams } from 'react-router-dom';
import {
    BsShuffle,
    BsTrophyFill,
    BsFileEarmarkPdf
} from 'react-icons/bs';
import { FaTimes, FaQuestionCircle, FaSave, FaBullhorn } from 'react-icons/fa';
import api from '../../api/axios';
import { toast } from 'react-toastify';
import { AppContext } from '../../context/AppContext';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const AdminPoomsae = ({ type: propType }) => {
    // --- SMART ROUTING ---
    const { type: paramType } = useParams();
    const activeType = propType || paramType || 'Prestasi';
    const currentType = activeType.charAt(0).toUpperCase() + activeType.slice(1);
    const isFestival = currentType === 'Festival';

    const { settings } = useContext(AppContext);

    const [athletes, setAthletes] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isGenerating, setIsGenerating] = useState(false);
    const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);

    const [scoreInputs, setScoreInputs] = useState({});
    const [flashRow, setFlashRow] = useState(null);
    const [confirmModal, setConfirmModal] = useState({ show: false, action: null, title: '', message: '' });

    // ====================================================================
    // HELPERS
    // ====================================================================
    const getEntryMembers = (athlete) => {
        if (!athlete) return [];

        if (Array.isArray(athlete.entry_members)) {
            return athlete.entry_members
                .map(member => String(member || '').trim())
                .filter(Boolean);
        }

        if (typeof athlete.entry_members === 'string' && athlete.entry_members.trim() !== '') {
            try {
                const parsed = JSON.parse(athlete.entry_members);

                if (Array.isArray(parsed)) {
                    return parsed
                        .map(member => String(member || '').trim())
                        .filter(Boolean);
                }
            } catch (error) {
                return athlete.entry_members
                    .split(/\s*(?:\/|\||,|\+|&|\bdan\b|\n)\s*/i)
                    .map(member => String(member || '').trim())
                    .filter(Boolean);
            }
        }

        return [];
    };

    const getDisplayName = (athlete) => {
        const members = getEntryMembers(athlete);

        if (members.length > 1) {
            return members.join(' / ');
        }

        return athlete?.name || '-';
    };

    const getGenderLabel = (gender) => {
        const value = String(gender || '').toLowerCase().trim();

        if (value === 'lelaki' || value === 'putra' || value === 'l') return 'PUTRA';
        if (value === 'perempuan' || value === 'putri' || value === 'p') return 'PUTRI';
        if (value === 'campuran' || value === 'mixed' || value === 'mix') return 'CAMPURAN';

        return gender || '-';
    };

    const getGenderCode = (gender) => {
        const value = String(gender || '').toLowerCase().trim();

        if (value === 'lelaki' || value === 'putra' || value === 'l') return 'L';
        if (value === 'perempuan' || value === 'putri' || value === 'p') return 'P';
        if (value === 'campuran' || value === 'mixed' || value === 'mix') return 'C';

        return '-';
    };

    const getCategoryLabel = (category) => {
        if (!category) return 'Individual';

        const value = String(category).trim();

        if (value.toLowerCase() === 'freestyle') return 'Freestyle';
        if (value.toLowerCase() === 'pair') return 'Pair';
        if (value.toLowerCase() === 'team') return 'Team';

        return value;
    };

    const getEventYear = () => {
        const rawSettings = settings?.config || settings || {};
        return parseInt(rawSettings.event_year || new Date().getFullYear());
    };

    // --- FETCH DATA ---
    const fetchPoomsaeData = async () => {
        setIsLoading(true);

        try {
            const response = await api.get(`/poomsae/fetch?type=${currentType}`);
            const data = response.data.data || [];

            setAthletes(data);

            const initialScores = {};
            data.forEach(a => {
                initialScores[a.uuid] = a.poomsae_score !== null && a.poomsae_score !== undefined ? a.poomsae_score : '';
            });

            setScoreInputs(initialScores);
        } catch (error) {
            toast.error("Gagal memuat data Poomsae.");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchPoomsaeData();
        // eslint-disable-next-line
    }, [currentType]);

    // ====================================================================
    // SINKRONISASI PENGELOMPOKAN & RANKING TAMPILAN
    // ====================================================================
    const groupedAthletes = useMemo(() => {
        if (!athletes || athletes.length === 0) return [];

        const groups = {};

        athletes.forEach(a => {
            const category = getCategoryLabel(a.poomsae_category);
            const genderLabel = getGenderLabel(a.gender);
            const beltLabel = a.belt || '-';

            const groupKey = isFestival
                ? `${a.division}-${category}-${beltLabel}-${a.gender}`
                : `${a.division}-${category}-${a.gender}`;

            if (!groups[groupKey]) {
                const title = isFestival
                    ? `${a.division} ${category} ${genderLabel} - SABUK ${beltLabel}`.toUpperCase()
                    : `${a.division} ${category} ${genderLabel}`.toUpperCase();

                groups[groupKey] = {
                    key: groupKey,
                    title,
                    division: a.division,
                    category,
                    gender: a.gender,
                    belt: beltLabel,
                    athletes: []
                };
            }

            groups[groupKey].athletes.push(a);
        });

        const resultGroups = [];

        for (const key in groups) {
            const group = groups[key];

            const mappedGroup = group.athletes.map(a => {
                const currentScore = scoreInputs[a.uuid] !== undefined && scoreInputs[a.uuid] !== ''
                    ? parseFloat(scoreInputs[a.uuid])
                    : -1;

                return { ...a, currentScore };
            });

            // Urutkan nilai tertinggi ke terendah untuk menentukan rank.
            const sortedByScore = [...mappedGroup].sort((a, b) => b.currentScore - a.currentScore);

            for (let i = 0; i < sortedByScore.length; i++) {
                if (sortedByScore[i].currentScore === -1) {
                    sortedByScore[i].rank = '-';
                } else {
                    if (i > 0 && sortedByScore[i].currentScore === sortedByScore[i - 1].currentScore) {
                        sortedByScore[i].rank = sortedByScore[i - 1].rank;
                    } else {
                        sortedByScore[i].rank = i + 1;
                    }
                }
            }

            // Kembalikan urutan berdasarkan nomor partai/undian agar rapi di UI.
            sortedByScore.sort((a, b) => (a.draw_number || 0) - (b.draw_number || 0));

            group.athletes = sortedByScore;
            resultGroups.push(group);
        }

        // Urutkan Grup.
        return resultGroups.sort((a, b) => {
            const divOrder = {
                'super pracadet': 0,
                'super pra-cadet': 0,
                'super pra cadet': 0,

                'pracadet a': 1,
                'pra-cadet a': 1,
                'pra cadet a': 1,

                'pracadet b': 2,
                'pra-cadet b': 2,
                'pra cadet b': 2,

                'pracadet c': 3,
                'pra-cadet c': 3,
                'pra cadet c': 3,

                'cadet': 4,
                'junior': 5,
                'senior': 6
            };

            const catOrder = {
                'individual': 1,
                'freestyle': 2,
                'pair': 3,
                'team': 4
            };

            const genOrder = {
                'lelaki': 1,
                'putra': 1,
                'l': 1,
                'perempuan': 2,
                'putri': 2,
                'p': 2,
                'campuran': 3
            };

            const beltOrder = {
                'putih': 1,
                'kuning': 2,
                'kuning strip': 3,
                'hijau': 4,
                'hijau strip': 5,
                'biru': 6,
                'biru strip': 7,
                'merah': 8,
                'merah strip': 9,
                'merah strip 1': 9,
                'merah strip 2': 9,
                'poom': 10,
                'hitam': 11
            };

            const getVal = (val) => String(val || '').toLowerCase().trim();

            const divA = divOrder[getVal(a.division)] ?? 99;
            const divB = divOrder[getVal(b.division)] ?? 99;
            if (divA !== divB) return divA - divB;

            const catA = catOrder[getVal(a.category)] ?? 99;
            const catB = catOrder[getVal(b.category)] ?? 99;
            if (catA !== catB) return catA - catB;

            const genA = genOrder[getVal(a.gender)] ?? 99;
            const genB = genOrder[getVal(b.gender)] ?? 99;
            if (genA !== genB) return genA - genB;

            if (isFestival) {
                const bA = beltOrder[getVal(a.belt)] ?? 99;
                const bB = beltOrder[getVal(b.belt)] ?? 99;
                if (bA !== bB) return bA - bB;
            }

            return 0;
        });
    }, [athletes, scoreInputs, isFestival]);

    // Format Data untuk Export PDF
    const flatAthletesWithRank = useMemo(() => {
        let flat = [];
        groupedAthletes.forEach(group => {
            flat = [...flat, ...group.athletes];
        });
        return flat;
    }, [groupedAthletes]);

    // --- HANDLERS ---
    const handleRandomizeDraw = () => {
        setConfirmModal({
            show: true,
            title: 'Acak Nomor Undian?',
            message: `Aksi ini akan mereset nomor giliran dan skor atlet Poomsae ${currentType}. Lanjutkan?`,
            action: async () => {
                setConfirmModal({ show: false, action: null });
                setIsGenerating(true);

                try {
                    const res = await api.post('/poomsae/randomize', { type: currentType });
                    toast.success(res.data.message || "Nomor undian berhasil diacak.");
                    fetchPoomsaeData();
                } catch (error) {
                    toast.error(error.response?.data?.message || "Gagal mengacak undian.");
                } finally {
                    setIsGenerating(false);
                }
            }
        });
    };

    const handleScoreChange = (uuid, value) => {
        setScoreInputs(prev => ({ ...prev, [uuid]: value }));
    };

    const handleSaveScore = async (uuid, isAutoSave = false) => {
        const score = scoreInputs[uuid];

        if (score === '' && !isAutoSave) {
            return toast.warning("Masukkan skor terlebih dahulu!");
        }

        try {
            await api.patch(`/poomsae/score/${uuid}`, { score });

            setFlashRow(uuid);
            setTimeout(() => setFlashRow(null), 1000);

            if (!isAutoSave) toast.success("Skor berhasil disimpan.");

            setAthletes(prev => prev.map(a => a.uuid === uuid ? {
                ...a,
                poomsae_score: score !== '' ? parseFloat(score) : null
            } : a));
        } catch (error) {
            toast.error(error.response?.data?.message || "Gagal menyimpan skor.");
        }
    };

    const handleAnnounceMatch = (athlete) => {
        const drawNumber = athlete.draw_number || 'Belum Diundi';
        const athleteName = getDisplayName(athlete);
        const teamName = athlete.team?.name || 'Kontingen Tidak Diketahui';
        const category = getCategoryLabel(athlete.poomsae_category);

        toast.info(`🔊 Mengumumkan: Partai P-${drawNumber} - ${athleteName}`);

        if ('speechSynthesis' in window) {
            window.speechSynthesis.cancel();

            const textToSpeak = `Panggilan kepada peserta Poomsae, kategori ${category}, partai nomor, ${drawNumber}. ${athleteName}, dari, ${teamName}. Silakan bersedia di arena sekarang. Terima kasih.`;

            const utterance = new SpeechSynthesisUtterance(textToSpeak);
            utterance.lang = 'id-ID';
            utterance.rate = 0.85;

            window.speechSynthesis.speak(utterance);
        } else {
            toast.warning("Browser Anda tidak mendukung fitur panggilan suara.");
        }
    };

    const generatePDF = () => {
        if (!groupedAthletes || groupedAthletes.length === 0) {
            return toast.warning("Tidak ada data untuk dicetak!");
        }

        setIsGeneratingPdf(true);
        const doc = new jsPDF('portrait');
        const printDate = new Date().toLocaleString('id-ID');

        let startY = 15;
        let pageNumber = 1;

        const addHeaderPage = (docInstance, pageNum) => {
            docInstance.setFontSize(10);
            docInstance.setFont('helvetica', 'normal');
            docInstance.text(`--- PAGE ${pageNum} ---`, docInstance.internal.pageSize.getWidth() / 2, 10, { align: 'center' });
        };

        addHeaderPage(doc, pageNumber);

        // LANGSUNG LOOPING DARI groupedAthletes
        groupedAthletes.forEach((group) => {
            const categoryName = group.title;
            const categoryAthletes = group.athletes;

            // Jika sisa ruang di halaman tidak cukup, buat halaman baru
            if (startY > 250) {
                doc.addPage();
                pageNumber++;
                addHeaderPage(doc, pageNumber);
                startY = 20;
            }

            doc.setFont('helvetica', 'bold');
            doc.setFontSize(11);
            // Ganti Header menjadi Kategori Kelas
            doc.text(`KATEGORI: ${categoryName}`, 14, startY);

            startY += 6;
            doc.setFontSize(9);
            doc.setFont('helvetica', 'normal');
            doc.text(`Total Entry: ${categoryAthletes.length} | Dicetak pada: ${printDate}`, 14, startY);

            startY += 8;

            const headCols = [["#", "NAMA / ENTRY", "KLUB / TIM", "JK", "PARTAI", "SKOR", "RANK"]];
            const colStyles = {
                0: { cellWidth: 10, halign: 'center' },
                1: { cellWidth: 50 },
                2: { cellWidth: 45 },
                3: { cellWidth: 10, halign: 'center' },
                4: { cellWidth: 20, halign: 'center' },
                5: { cellWidth: 20, halign: 'center' },
                6: { cellWidth: 15, halign: 'center' }
            };

            const bodyRows = categoryAthletes.map((a, i) => {
                const currentScore = scoreInputs[a.uuid] !== undefined && scoreInputs[a.uuid] !== ''
                    ? scoreInputs[a.uuid]
                    : (a.poomsae_score || '-');

                return [
                    i + 1,
                    getDisplayName(a).toUpperCase(),
                    (a.team?.name || 'UNATTACHED').toUpperCase(), // Masukkan nama tim ke dalam tabel
                    getGenderCode(a.gender),
                    `P-${String(a.draw_number || 0).padStart(3, '0')}`,
                    currentScore,
                    a.rank || '-'
                ];
            });

            autoTable(doc, {
                startY,
                head: headCols,
                body: bodyRows,
                theme: 'grid',
                styles: { font: 'helvetica', fontSize: 8, textColor: [0, 0, 0], lineColor: [0, 0, 0], lineWidth: 0.1 },
                headStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0], fontStyle: 'bold', halign: 'center' },
                columnStyles: colStyles
            });

            startY = doc.lastAutoTable.finalY + 12;
        });

        const pageCount = doc.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            doc.setFontSize(10);
            doc.text(`Halaman ${i} - Laporan ${currentType} Poomsae`, 14, doc.internal.pageSize.getHeight() - 10);
        }

        doc.save(`StartList_Poomsae_${currentType}_${new Date().toISOString().split('T')[0]}.pdf`);
        setIsGeneratingPdf(false);
        toast.success("PDF berhasil di-generate!");
    };

    return (
        <div className="container-fluid p-0 animate-fade-in" style={{ backgroundColor: '#f4f7fe', minHeight: '100vh', paddingBottom: '50px' }}>
            {/* HEADER */}
            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3 p-4 pb-0">
                <div>
                    <h4 className="fw-bold mb-1 text-dark d-flex align-items-center gap-2">
                        <BsTrophyFill className="text-primary" /> Penilaian Poomsae {currentType}
                    </h4>
                    <p className="text-muted small mb-0">Kelola nomor undian, input skor juri, dan lihat peringkat otomatis.</p>
                </div>

                <div className="d-flex gap-2">
                    <button
                        onClick={handleRandomizeDraw}
                        className="btn btn-warning text-dark fw-bold rounded-pill px-4 shadow-sm hover-scale d-flex align-items-center gap-2"
                        disabled={isGenerating || athletes.length === 0}
                    >
                        {isGenerating ? <div className="spinner-border spinner-border-sm"></div> : <BsShuffle />}
                        Acak Undian
                    </button>

                    <button
                        onClick={generatePDF}
                        disabled={isGeneratingPdf || athletes.length === 0}
                        className="btn btn-danger text-white fw-bold rounded-pill px-4 shadow-sm hover-scale d-flex align-items-center gap-2"
                    >
                        {isGeneratingPdf ? <div className="spinner-border spinner-border-sm"></div> : <BsFileEarmarkPdf />}
                        Cetak PDF
                    </button>
                </div>
            </div>

            {/* KONTEN UTAMA */}
            <div className="px-4">
                {isLoading ? (
                    <div className="text-center py-5 my-5">
                        <div className="spinner-border text-primary"></div>
                    </div>
                ) : groupedAthletes.length === 0 ? (
                    <div className="text-center py-5 my-5 text-muted bg-white rounded-4 shadow-sm border p-5">
                        <BsTrophyFill size={50} className="mb-3 opacity-25" />
                        <h6 className="fw-bold">Belum ada atlet Poomsae</h6>
                        <p className="small">Pastikan atlet Poomsae {currentType} sudah terdaftar pada kategori ini.</p>
                    </div>
                ) : (
                    groupedAthletes.map((group) => (
                        <div key={group.key} className="card border-0 shadow-sm rounded-4 overflow-hidden mb-4 animate-fade-in bg-white">

                            {/* Header Grup */}
                            <div className="p-3 px-4 d-flex justify-content-between align-items-center" style={{ backgroundColor: '#253b70', color: 'white' }}>
                                <div>
                                    <h5 className="fw-black mb-1 text-uppercase tracking-wider">{group.title}</h5>
                                    <div className="fw-bold text-uppercase" style={{ fontSize: '0.75rem', color: '#93a5cf' }}>
                                        ARENA: H1 - POOMSAE
                                    </div>
                                </div>

                                <div className="badge rounded-pill d-flex align-items-center justify-content-center fw-bold shadow-sm" style={{ backgroundColor: '#162854', padding: '8px 16px', fontSize: '0.8rem' }}>
                                    {group.athletes.length} ENTRY
                                </div>
                            </div>

                            {/* Tabel Daftar Atlet */}
                            <div className="table-responsive">
                                <table className="table align-middle mb-0 text-nowrap">
                                    <thead style={{ backgroundColor: '#f8fafc' }}>
                                        <tr>
                                            <th className="py-3 ps-4 text-muted small fw-bold" width="10%">PARTAI</th>
                                            <th className="py-3 text-muted small fw-bold" width="30%">NAMA / ENTRY</th>
                                            <th className="py-3 text-muted small fw-bold" width="25%">KLUB / TIM</th>
                                            <th className="py-3 text-center text-muted small fw-bold" width="15%">INPUT NILAI</th>
                                            <th className="py-3 text-center text-muted small fw-bold" width="10%">RANK</th>
                                            <th className="py-3 pe-4 text-end text-muted small fw-bold" width="10%">AKSI</th>
                                        </tr>
                                    </thead>

                                    <tbody className="border-top-0">
                                        {group.athletes.map((athlete) => {
                                            const aid = athlete.uuid;
                                            const isFlashed = flashRow === aid;
                                            const isScored = athlete.poomsae_score !== null && athlete.poomsae_score !== undefined;
                                            const rankNum = athlete.rank;
                                            const members = getEntryMembers(athlete);
                                            const displayName = getDisplayName(athlete);

                                            return (
                                                <tr key={aid} className={`hover-up-row ${isFlashed ? 'bg-success bg-opacity-10 transition-none' : 'transition-all'}`}>

                                                    {/* Kolom Partai */}
                                                    <td className="ps-4 py-3 border-bottom">
                                                        <div className="badge bg-primary bg-opacity-10 text-primary px-3 py-2 rounded-3 fw-bold" style={{ fontSize: '0.85rem' }}>
                                                            P-{String(athlete.draw_number || 0).padStart(3, '0')}
                                                        </div>
                                                    </td>

                                                    {/* Kolom Nama */}
                                                    <td className="border-bottom">
                                                        <div className="fw-black text-dark fs-6 text-uppercase">{displayName}</div>

                                                        <div className="small text-muted mt-1 d-flex align-items-center gap-2 flex-wrap">
                                                            <span>{getGenderLabel(athlete.gender)}</span>
                                                            <span>•</span>
                                                            <span>{getCategoryLabel(athlete.poomsae_category)}</span>

                                                            {members.length > 1 && (
                                                                <span className="badge bg-info bg-opacity-10 text-info border border-info border-opacity-25">
                                                                    {members.length} Nama / 1 Entry
                                                                </span>
                                                            )}
                                                        </div>
                                                    </td>

                                                    {/* Kolom Klub */}
                                                    <td className="border-bottom">
                                                        <div className="fw-bold text-muted text-uppercase">{athlete.team?.name || '-'}</div>
                                                    </td>

                                                    {/* Kolom Input Nilai */}
                                                    <td className="border-bottom text-center">
                                                        <div className="input-group input-group-sm mx-auto" style={{ maxWidth: '140px' }}>
                                                            <input
                                                                type="number"
                                                                step="0.01"
                                                                min="0"
                                                                max="100"
                                                                className={`form-control text-center fw-bold py-2 shadow-sm ${isScored ? 'text-primary' : 'text-dark'}`}
                                                                placeholder="0.00"
                                                                style={{
                                                                    fontSize: '1rem',
                                                                    borderRadius: '8px',
                                                                    border: `2px solid ${isScored ? '#bfdbfe' : '#e2e8f0'}`,
                                                                    backgroundColor: isScored ? '#f0f9ff' : '#ffffff'
                                                                }}
                                                                value={scoreInputs[aid] !== undefined ? scoreInputs[aid] : ''}
                                                                onChange={(e) => handleScoreChange(aid, e.target.value)}
                                                                onBlur={() => handleSaveScore(aid, true)}
                                                                onKeyDown={(e) => {
                                                                    if (e.key === 'Enter') handleSaveScore(aid, false);
                                                                }}
                                                            />
                                                        </div>
                                                    </td>

                                                    {/* Kolom Ranking */}
                                                    <td className="border-bottom text-center">
                                                        <div
                                                            className="d-inline-flex justify-content-center align-items-center fw-bold shadow-sm"
                                                            style={{
                                                                width: '40px',
                                                                height: '40px',
                                                                borderRadius: '10px',
                                                                fontSize: '1rem',
                                                                backgroundColor: rankNum === 1 ? '#fef08a' : '#f1f5f9',
                                                                color: rankNum === 1 ? '#854d0e' : '#64748b'
                                                            }}
                                                        >
                                                            {rankNum}
                                                        </div>
                                                    </td>

                                                    {/* Kolom Aksi */}
                                                    <td className="pe-4 text-end border-bottom">
                                                        <div className="d-flex justify-content-end align-items-center gap-2">
                                                            <button
                                                                onClick={() => handleAnnounceMatch(athlete)}
                                                                className="btn btn-light border text-primary rounded-circle shadow-sm hover-scale p-0 d-flex justify-content-center align-items-center"
                                                                style={{ width: '38px', height: '38px' }}
                                                                title="Panggil Peserta"
                                                            >
                                                                <FaBullhorn />
                                                            </button>

                                                            <button
                                                                onClick={() => handleSaveScore(aid, false)}
                                                                className="btn btn-primary rounded-circle shadow-sm hover-scale p-0 d-flex justify-content-center align-items-center"
                                                                style={{ width: '38px', height: '38px' }}
                                                                title="Simpan Skor"
                                                            >
                                                                <FaSave />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* MODAL CONFIRMATION */}
            {confirmModal.show && createPortal(
                <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center" style={{ zIndex: 1200, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(3px)' }}>
                    <div className="card border-0 shadow-lg animate-pop-in" style={{ width: '90%', maxWidth: '400px', borderRadius: '24px' }}>
                        <div className="card-body p-5 text-center">
                            <button
                                type="button"
                                className="btn btn-sm btn-light rounded-circle position-absolute top-0 end-0 m-3"
                                onClick={() => setConfirmModal({ show: false, action: null })}
                            >
                                <FaTimes />
                            </button>

                            <div className="mb-3">
                                <FaQuestionCircle size={55} className="text-warning opacity-75" />
                            </div>

                            <h5 className="fw-bold text-dark mb-2">{confirmModal.title}</h5>
                            <p className="text-muted small mb-4">{confirmModal.message}</p>

                            <div className="d-flex gap-2 justify-content-center">
                                <button className="btn btn-light border rounded-pill px-4 fw-bold hover-scale" onClick={() => setConfirmModal({ show: false, action: null })}>
                                    Batal
                                </button>
                                <button className="btn btn-primary rounded-pill px-4 fw-bold shadow-sm hover-scale" onClick={confirmModal.action}>
                                    Ya, Acak Sekarang
                                </button>
                            </div>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* CSS STYLE */}
            <style>{`
                .animate-fade-in { animation: fadeIn 0.4s ease-out forwards; }
                .animate-pop-in { animation: popIn 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards; }
                .transition-all { transition: all 0.2s ease-in-out; }
                .transition-none { transition: none !important; }
                .hover-scale { transition: transform 0.2s; }
                .hover-scale:hover { transform: scale(1.05); }
                .hover-up-row { transition: background-color 0.2s; }
                .hover-up-row:hover { background-color: #f8f9fa !important; }
                input[type="number"]::-webkit-inner-spin-button,
                input[type="number"]::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                @keyframes popIn { 
                    0% { transform: scale(0.9); opacity: 0; } 
                    100% { transform: scale(1); opacity: 1; } 
                }
            `}</style>
        </div>
    );
};

export default AdminPoomsae;